import { Controller } from "@hotwired/stimulus"
import zxcvbn from "zxcvbn"

export default class extends Controller {
  static targets = [
    "password",
    "confirmation",
    "strengthBar",
    "strengthText",
    "lengthCheck",
    "matchCheck",
    "toggleIcon"
  ]

  connect() {
    this.isVisible = false
    this.updateStrength()
    this.updateChecklist()
  }

  toggleVisibility() {
    this.isVisible = !this.isVisible
    const inputType = this.isVisible ? "text" : "password"

    // Toggle both password inputs
    this.passwordTarget.type = inputType
    this.confirmationTarget.type = inputType

    // Update all toggle icons
    this.toggleIconTargets.forEach(iconContainer => {
      const icons = iconContainer.querySelectorAll("svg")
      if (this.isVisible) {
        icons[0].classList.add("hidden")
        icons[1].classList.remove("hidden")
      } else {
        icons[0].classList.remove("hidden")
        icons[1].classList.add("hidden")
      }
    })
  }

  checkPassword() {
    this.updateStrength()
    this.updateChecklist()
  }

  checkConfirmation() {
    this.updateChecklist()
  }

  updateStrength() {
    const password = this.passwordTarget.value
    const strength = this.calculateStrength(password)
    
    // Update strength bar
    this.strengthBarTarget.style.width = `${strength.percentage}%`
    this.strengthBarTarget.className = `h-2 rounded-full transition-all duration-300 ${strength.colorClass}`
    
    // Update strength text
    this.strengthTextTarget.textContent = strength.label
    this.strengthTextTarget.className = `text-sm font-medium ${strength.textColorClass}`
  }

  updateChecklist() {
    const password = this.passwordTarget.value
    const confirmation = this.confirmationTarget.value
    
    // Check length requirement
    if (password.length >= 8) {
      this.updateCheckItem(this.lengthCheckTarget, true)
    } else {
      this.updateCheckItem(this.lengthCheckTarget, false)
    }
    
    // Check match requirement
    if (password.length > 0 && confirmation.length > 0 && password === confirmation) {
      this.updateCheckItem(this.matchCheckTarget, true)
    } else if (confirmation.length > 0) {
      this.updateCheckItem(this.matchCheckTarget, false)
    } else {
      this.resetCheckItem(this.matchCheckTarget)
    }
  }

  updateCheckItem(element, isValid) {
    const pendingIcon = element.querySelector('[data-icon="pending"]')
    const validIcon = element.querySelector('[data-icon="valid"]')
    const invalidIcon = element.querySelector('[data-icon="invalid"]')
    const text = element.querySelector('span:last-child')
    
    // Hide all icons first
    pendingIcon.classList.add('hidden')
    validIcon.classList.add('hidden')
    invalidIcon.classList.add('hidden')
    
    if (isValid) {
      validIcon.classList.remove('hidden')
      text.classList.remove('text-gray-600', 'text-red-600')
      text.classList.add('text-green-600')
    } else {
      invalidIcon.classList.remove('hidden')
      text.classList.remove('text-gray-600', 'text-green-600')
      text.classList.add('text-red-600')
    }
  }

  resetCheckItem(element) {
    const pendingIcon = element.querySelector('[data-icon="pending"]')
    const validIcon = element.querySelector('[data-icon="valid"]')
    const invalidIcon = element.querySelector('[data-icon="invalid"]')
    const text = element.querySelector('span:last-child')
    
    // Show only pending icon
    pendingIcon.classList.remove('hidden')
    validIcon.classList.add('hidden')
    invalidIcon.classList.add('hidden')
    
    text.classList.remove('text-green-600', 'text-red-600')
    text.classList.add('text-gray-600')
  }

  calculateStrength(password) {
    if (password.length === 0) {
      return {
        percentage: 0,
        label: '',
        colorClass: 'bg-gray-300',
        textColorClass: 'text-gray-500'
      }
    }

    // Use zxcvbn for realistic password strength estimation
    // It detects common passwords, dictionary words, keyboard patterns, etc.
    const result = zxcvbn(password)
    
    // zxcvbn returns a score from 0-4
    // 0: too guessable (risky password)
    // 1: very guessable (protection from throttled online attacks)
    // 2: somewhat guessable (protection from unthrottled online attacks)
    // 3: safely unguessable (moderate protection from offline slow-hash scenario)
    // 4: very unguessable (strong protection from offline slow-hash scenario)
    
    const strengthLevels = [
      {
        percentage: 20,
        label: 'Very Weak',
        colorClass: 'bg-red-600',
        textColorClass: 'text-red-600'
      },
      {
        percentage: 40,
        label: 'Weak',
        colorClass: 'bg-red-500',
        textColorClass: 'text-red-600'
      },
      {
        percentage: 60,
        label: 'Fair',
        colorClass: 'bg-orange-500',
        textColorClass: 'text-orange-600'
      },
      {
        percentage: 80,
        label: 'Good',
        colorClass: 'bg-blue-500',
        textColorClass: 'text-blue-600'
      },
      {
        percentage: 100,
        label: 'Strong',
        colorClass: 'bg-green-500',
        textColorClass: 'text-green-600'
      }
    ]
    
    return strengthLevels[result.score]
  }
}

